import React, { createContext, type FC, type PropsWithChildren, useContext, useEffect } from 'react'

import { assert } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../../domain'

import { BulkModalScopeProvider } from './Scope'

type RecordPanelContent = Readonly<{
    /** Closes on the user's behalf, giving the focus back to the record's row */
    close: VoidFunction
    /** Closes without moving the focus, e.g. once the record is gone */
    dismiss: VoidFunction
    /** If the list's selection is the record alone */
    openRecordSelected: boolean
    /** Names the panel after its record, once the record is loaded */
    showTitle: (title: string) => void
    /** The record whose own actions the panel's modals take over from the list's, `null` once they're unmounted */
    takeOverModals: (id: string | null) => void
}>

const RecordPanelContentContext = createContext<RecordPanelContent | null>(null)

export const RecordPanelContentProvider: FC<Readonly<{ value: RecordPanelContent }> & PropsWithChildren> = ({ value, children }) => (
    <RecordPanelContentContext.Provider value={value}>{children}</RecordPanelContentContext.Provider>
)

/**
 * @returns the panel the content is rendered in, `null` outside of one
 */
export const useRecordPanelContent = (): RecordPanelContent | null => useContext(RecordPanelContentContext)

/**
 * Wraps the modals of the record open in the panel, which take its own actions over from the list's modals while mounted
 */
export const RecordPanelModals: FC<Readonly<{ id: string }> & PropsWithChildren> = ({ id, children }) => {
    const content = useRecordPanelContent()
    assert(content, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Record panel modals are rendered outside of a record panel', { id }))

    const { takeOverModals, openRecordSelected } = content

    useEffect(() => {
        takeOverModals(id)
        return () => takeOverModals(null)
    }, [takeOverModals, id])

    return (
        <BulkModalScopeProvider owner="panel" openId={id} openRecordSelected={openRecordSelected}>
            {children}
        </BulkModalScopeProvider>
    )
}
